import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { notesService } from '../adapters/notes';
import toast from '../lib/toast';

const MIN_RECORDING_MS = 3 * 60 * 1000;
const MAX_RECORDING_MS = 65 * 60 * 1000;
const MAX_CONCURRENT = 3;
const BAR_COUNT = 32;

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function detectMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  for (const c of candidates) {
    try { if (MediaRecorder.isTypeSupported?.(c)) return c; } catch {}
  }
  return 'audio/webm';
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export interface RecordingSession {
  session_id: string;
  patientId: string;
  patientName: string;
  status: 'recording' | 'paused' | 'uploading' | 'error';
  noteType: 'INITIAL' | 'FOLLOWUP';
  previousSessionId: string | null;
  totalBytes: number;
  accumulatedMs: number;
  segmentStartTs: number | null;
  createdAt: number;
}

interface RecordingContextValue {
  activeSessionId: string | null;
  sessions: Record<string, RecordingSession>;
  actions: {
    startRecording: (patientId: string, patientName: string, extra?: { noteType?: string; previousSessionId?: string | null }) => Promise<string | undefined>;
    pauseRecording: (sessionId: string) => void;
    resumeRecording: (sessionId: string) => Promise<void>;
    stopRecording: (sessionId: string) => Promise<void>;
    discardSession: (sessionId: string) => Promise<void>;
  };
  utils: {
    computeDurationMs: (session: RecordingSession) => number;
    formatBytes: (bytes: number) => string;
    formatDuration: (ms: number) => string;
  };
  visualizerPeaks: number[] | null;
  visualizerMeta: { speaking: boolean } | null;
  visualizerRawLevels: number[] | null;
}

const defaultValue: RecordingContextValue = {
  activeSessionId: null,
  sessions: {},
  actions: {
    startRecording: async () => undefined,
    pauseRecording: () => {},
    resumeRecording: async () => {},
    stopRecording: async () => {},
    discardSession: async () => {},
  },
  utils: { computeDurationMs: () => 0, formatBytes, formatDuration },
  visualizerPeaks: null,
  visualizerMeta: null,
  visualizerRawLevels: null,
};

const RecordingContext = createContext<RecordingContextValue>(defaultValue);

export const useRecording = () => useContext(RecordingContext);

export const RecordingProvider = ({ children }: { children: React.ReactNode }) => {
  const [sessions, setSessions] = useState<Record<string, RecordingSession>>({});
  const sessionsRef = useRef<Record<string, RecordingSession>>({});
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Map<string, Blob[]>>(new Map());
  const stopPromiseRef = useRef<Promise<void> | null>(null);
  const stopResolveRef = useRef<(() => void) | null>(null);
  const mimeRef = useRef('audio/webm');
  const maxWarnedRef = useRef<Set<string>>(new Set());

  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const peaksRef = useRef<number[]>(new Array(BAR_COUNT).fill(0));
  const levelsRef = useRef<number[]>(new Array(BAR_COUNT).fill(0));
  const rollingMaxRef = useRef(0.05);
  const speakingRef = useRef(false);
  const silenceWindowRef = useRef(0);

  const [visualizerPeaks, setVisualizerPeaks] = useState<number[] | null>(null);
  const [visualizerRawLevels, setVisualizerRawLevels] = useState<number[] | null>(null);
  const [visualizerMeta, setVisualizerMeta] = useState<{ speaking: boolean } | null>(null);

  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);

  const computeDurationMs = useCallback((s: RecordingSession): number => {
    if (!s) return 0;
    if (s.status === 'recording' && s.segmentStartTs) {
      return s.accumulatedMs + (Date.now() - s.segmentStartTs);
    }
    return s.accumulatedMs;
  }, []);

  const stopVisualizer = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    try { if (audioCtxRef.current?.state !== 'closed') audioCtxRef.current?.close(); } catch {}
    audioCtxRef.current = null;
    analyserRef.current = null;
    setVisualizerPeaks(null);
    setVisualizerRawLevels(null);
    setVisualizerMeta(null);
  }, []);

  const startVisualizer = useCallback((stream: MediaStream) => {
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx = new AC() as AudioContext;
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      analyserRef.current = analyser;
      source.connect(analyser);
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});

      const freqData = new Uint8Array(analyser.frequencyBinCount);
      const timeData = new Uint8Array(analyser.fftSize);
      const step = Math.max(1, Math.floor(freqData.length / BAR_COUNT));
      let silenceFrames = 0;

      const loop = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(freqData);
        analyserRef.current.getByteTimeDomainData(timeData);

        const rawValues = new Array(BAR_COUNT).fill(0);
        let frameMax = 0;
        for (let i = 0; i < BAR_COUNT; i++) {
          let sum = 0, c = 0;
          for (let j = i * step; j < (i + 1) * step && j < freqData.length; j++) { sum += freqData[j]; c++; }
          const avgFreq = c ? sum / c : 0;
          let rmsSum = 0, rmsC = 0;
          const tStart = i * step;
          for (let j = tStart; j < Math.min(timeData.length, tStart + step); j += 2) {
            const v = (timeData[j] - 128) / 128; rmsSum += v * v; rmsC++;
          }
          const rms = rmsC ? Math.sqrt(rmsSum / rmsC) : 0;
          const combined = rms * 0.75 + (avgFreq / 255) * 0.25;
          rawValues[i] = combined;
          if (combined > frameMax) frameMax = combined;
        }

        const newRoll = Math.max(rollingMaxRef.current * 0.995, frameMax, 0.05);
        rollingMaxRef.current = newRoll;

        const avgRaw = rawValues.reduce((a, b) => a + b, 0) / rawValues.length;
        const isSpeaking = (avgRaw / (newRoll || 0.001)) > 0.22 || (frameMax / (newRoll || 0.001)) > 0.32;
        if (isSpeaking) { speakingRef.current = true; silenceWindowRef.current = 0; }
        else { silenceWindowRef.current++; if (silenceWindowRef.current > 120) speakingRef.current = false; }

        const normValues = rawValues.map(raw => Math.min(1, Math.max(0, raw / (newRoll || 0.001))));
        const next = normValues.map((norm, i) => {
          let visual = Math.pow(norm, 0.6);
          if (!speakingRef.current && visual < 0.04) visual += Math.random() * 0.025;
          visual = Math.max(0.02, visual);
          const prev = levelsRef.current[i] || 0;
          const attack = visual > prev ? (speakingRef.current ? 0.65 : 0.5) : 0.25;
          const smoothed = prev + (visual - prev) * attack;
          if (smoothed > peaksRef.current[i]) peaksRef.current[i] = smoothed;
          else peaksRef.current[i] = Math.max(0, peaksRef.current[i] - 0.012);
          return smoothed;
        });

        const avgLevel = next.reduce((a, b) => a + b, 0) / next.length;
        if (!speakingRef.current && avgLevel < 0.04) {
          silenceFrames++;
          if (silenceFrames > 180) {
            const t = performance.now() / 1000;
            for (let i = 0; i < next.length; i++) {
              next[i] = Math.max(next[i], 0.02 + (Math.sin(t * 1.2 + i * 0.35) + 1) / 2 * 0.10);
            }
          }
        } else { silenceFrames = 0; }

        levelsRef.current = next;
        if (performance.now() % 16 < 1) {
          setVisualizerPeaks([...peaksRef.current]);
          setVisualizerRawLevels([...normValues]);
          setVisualizerMeta({ speaking: speakingRef.current });
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch {}
  }, []);

  const initMediaRecorder = useCallback(async (sessionId: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mimeRef.current = detectMime();
    const mr = new MediaRecorder(stream, { mimeType: mimeRef.current });
    mediaRecorderRef.current = mr;
    startVisualizer(stream);
    stopPromiseRef.current = new Promise<void>(resolve => { stopResolveRef.current = resolve; });
    mr.onstop = () => { stopResolveRef.current?.(); };
    mr.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      const arr = chunksRef.current.get(sessionId) || [];
      arr.push(e.data);
      chunksRef.current.set(sessionId, arr);
      setSessions(prev => {
        const cur = prev[sessionId]; if (!cur) return prev;
        return { ...prev, [sessionId]: { ...cur, totalBytes: cur.totalBytes + e.data.size } };
      });
    };
    mr.start();
  }, [startVisualizer]);

  const pauseRecording = useCallback((sessionId: string) => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      try { mr.requestData(); } catch {}
      try { mr.stop(); } catch {}
      try { mr.stream.getTracks().forEach(t => t.stop()); } catch {}
      mediaRecorderRef.current = null;
    }
    stopVisualizer();
    setActiveSessionId(prev => prev === sessionId ? null : prev);
    setSessions(prev => {
      const cur = prev[sessionId]; if (!cur) return prev;
      const extra = cur.status === 'recording' && cur.segmentStartTs
        ? { accumulatedMs: Math.min(cur.accumulatedMs + (Date.now() - cur.segmentStartTs), MAX_RECORDING_MS) }
        : {};
      return { ...prev, [sessionId]: { ...cur, ...extra, segmentStartTs: null, status: 'paused' } };
    });
  }, [stopVisualizer]);

  const startRecording = useCallback(async (
    patientId: string,
    patientName: string,
    extra?: { noteType?: string; previousSessionId?: string | null },
  ): Promise<string | undefined> => {
    if (!patientId) { toast.error('Select a patient first'); return; }
    const concurrent = Object.values(sessionsRef.current).filter(s => s.status === 'recording' || s.status === 'paused').length;
    if (concurrent >= MAX_CONCURRENT) {
      toast.error('Maximum 3 concurrent recordings. Stop or discard one first.');
      return;
    }
    const activeId = Object.keys(sessionsRef.current).find(id => sessionsRef.current[id].status === 'recording');
    if (activeId) pauseRecording(activeId);
    try {
      const sessionId = generateId();
      const noteType: 'INITIAL' | 'FOLLOWUP' =
        extra?.noteType === 'FOLLOWUP' || extra?.noteType === 'FOLLOWUP_NOTE' ? 'FOLLOWUP' : 'INITIAL';
      const session: RecordingSession = {
        session_id: sessionId,
        patientId,
        patientName,
        status: 'recording',
        noteType,
        previousSessionId: extra?.previousSessionId ?? null,
        totalBytes: 0,
        accumulatedMs: 0,
        segmentStartTs: Date.now(),
        createdAt: Date.now(),
      };
      chunksRef.current.set(sessionId, []);
      setSessions(prev => ({ ...prev, [sessionId]: session }));
      setActiveSessionId(sessionId);
      await initMediaRecorder(sessionId);
      return sessionId;
    } catch (e: any) {
      toast.error(e?.name === 'NotAllowedError' ? 'Microphone permission denied' : 'Failed to start recording');
    }
  }, [pauseRecording, initMediaRecorder]);

  const resumeRecording = useCallback(async (sessionId: string) => {
    const s = sessionsRef.current[sessionId];
    if (!s || s.status !== 'paused') return;
    if (computeDurationMs(s) >= MAX_RECORDING_MS) {
      toast.error('Recording is at the 65 minute limit. Stop to upload or discard.');
      return;
    }
    const activeId = Object.keys(sessionsRef.current).find(id => sessionsRef.current[id].status === 'recording');
    if (activeId && activeId !== sessionId) pauseRecording(activeId);
    setSessions(prev => {
      const cur = prev[sessionId]; if (!cur) return prev;
      return { ...prev, [sessionId]: { ...cur, status: 'recording', segmentStartTs: Date.now() } };
    });
    setActiveSessionId(sessionId);
    await initMediaRecorder(sessionId);
  }, [computeDurationMs, pauseRecording, initMediaRecorder]);

  const stopRecording = useCallback(async (sessionId: string) => {
    const s = sessionsRef.current[sessionId];
    if (!s) return;
    const dur = computeDurationMs(s);
    if (dur < MIN_RECORDING_MS) {
      toast.error('Recording must be at least 3 minutes. Pausing — continue or discard.');
      if (s.status === 'recording') pauseRecording(sessionId);
      return;
    }
    if (s.status === 'recording') {
      pauseRecording(sessionId);
      if (stopPromiseRef.current) {
        try { await stopPromiseRef.current; } catch {}
        stopPromiseRef.current = null;
      }
    }
    setSessions(prev => {
      const cur = prev[sessionId]; if (!cur) return prev;
      return { ...prev, [sessionId]: { ...cur, status: 'uploading' } };
    });
    try {
      const chunks = chunksRef.current.get(sessionId) || [];
      if (chunks.length === 0) {
        toast.error('No audio captured. Was microphone permission denied?');
        setSessions(prev => {
          const cur = prev[sessionId]; if (!cur) return prev;
          return { ...prev, [sessionId]: { ...cur, status: 'error' } };
        });
        return;
      }
      const blob = new Blob(chunks, { type: mimeRef.current });
      const sess = sessionsRef.current[sessionId];
      await notesService.createNote(sess.patientId, blob, sess.noteType);
      chunksRef.current.delete(sessionId);
      setSessions(prev => { const ns = { ...prev }; delete ns[sessionId]; return ns; });
      setActiveSessionId(prev => prev === sessionId ? null : prev);
      toast.success('Recording uploaded — your note is being generated');
    } catch (e: any) {
      toast.error(e?.response?.data?.msg || 'Upload failed');
      setSessions(prev => {
        const cur = prev[sessionId]; if (!cur) return prev;
        return { ...prev, [sessionId]: { ...cur, status: 'error' } };
      });
    }
  }, [computeDurationMs, pauseRecording]);

  const discardSession = useCallback(async (sessionId: string) => {
    const s = sessionsRef.current[sessionId];
    if (!s) return;
    if (!window.confirm('Discard this recording? Audio will be permanently deleted.')) return;
    if (s.status === 'recording') pauseRecording(sessionId);
    chunksRef.current.delete(sessionId);
    setSessions(prev => { const ns = { ...prev }; delete ns[sessionId]; return ns; });
    setActiveSessionId(prev => prev === sessionId ? null : prev);
    toast.success('Recording discarded');
  }, [pauseRecording]);

  useEffect(() => {
    const id = setInterval(() => {
      const activeId = Object.keys(sessionsRef.current).find(id => sessionsRef.current[id].status === 'recording');
      if (!activeId) return;
      const s = sessionsRef.current[activeId];
      if (computeDurationMs(s) >= MAX_RECORDING_MS && !maxWarnedRef.current.has(activeId)) {
        maxWarnedRef.current.add(activeId);
        toast.error('Recording reached the 65 minute limit and has been paused.');
        pauseRecording(activeId);
        return;
      }
      setSessions(prev => {
        const cur = prev[activeId]; if (!cur) return prev;
        return { ...prev, [activeId]: { ...cur } };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [computeDurationMs, pauseRecording]);

  return (
    <RecordingContext.Provider value={{
      activeSessionId,
      sessions,
      actions: { startRecording, pauseRecording, resumeRecording, stopRecording, discardSession },
      utils: { computeDurationMs, formatBytes, formatDuration },
      visualizerPeaks,
      visualizerMeta,
      visualizerRawLevels,
    }}>
      {children}
    </RecordingContext.Provider>
  );
};
